import { Link } from "react-router-dom";
import { Plane, Truck, Ship, Package, ArrowRight, ShieldCheck, MapPinned, BellRing, Globe2, Languages, BadgeCheck, Building2, Zap, Lock, Network, Phone } from "lucide-react";
import { TrackSearch } from "@/components/TrackSearch";
import { SITE } from "@/lib/api";

const SAMPLES = ["ISH-9842-7104", "ISH-7890-1234", "ISH-1102-3349"];

function Hero() {
  return (
    <section className="relative bg-[#0A192F] overflow-hidden grain">
      <img src="/images/hero.jpg" alt="Container ship at sea" className="absolute inset-0 w-full h-full object-cover opacity-35" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/85 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
        <div className="max-w-2xl space-y-8">
          <p className="rise inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00]">
            <span className="h-2 w-2 rounded-full bg-[#FF6B00] pulse-dot" /> Live consignment tracking
          </p>
          <h1 className="rise d1 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white" data-testid="hero-title">
            Every consignment,<br />tracked to the <span className="text-[#FF6B00]">doorstep.</span>
          </h1>
          <p className="rise d2 text-base md:text-lg text-slate-300 max-w-xl">Book shipments online, follow each checkpoint on a live map, and get email updates the moment your package moves.</p>
          <div className="rise d3"><TrackSearch dark /></div>
          <div className="rise d4 flex flex-wrap items-center gap-2 text-sm text-slate-400">
            <span>Try a sample:</span>
            {SAMPLES.map((s) => (
              <Link key={s} to={`/track/${s}`} className="font-mono text-xs px-2.5 py-1 border border-white/20 text-slate-200 hover:border-[#FF6B00] hover:text-[#FF6B00] rounded-sm transition-colors" data-testid={`sample-tracking-${s}`}>{s}</Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  { icon: Ship, title: "Ocean Freight", text: "Cost-effective cargo for heavy and bulk loads.", img: "/images/cargo-ship.jpg", span: "md:col-span-2 md:row-span-2" },
  { icon: Plane, title: "Priority Air", text: "Fastest international lanes for urgent consignments." },
  { icon: Truck, title: "Express Courier", text: "Door-to-door parcels with signature delivery." },
  { icon: Package, title: "Packaging & Storage", text: "Professional packing, secure storage and consolidation at our hubs.", img: "/images/packaging.jpg", span: "md:col-span-2" },
];

function Services() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <div className="max-w-xl mb-12">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00] mb-3">Services</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">Shipping options for every load</h2>
      </div>
      <div className="grid md:grid-cols-4 md:auto-rows-[220px] gap-4">
        {SERVICES.map(({ icon: Icon, title, text, img, span }) => (
          <div key={title} className={`group relative overflow-hidden rounded-sm border border-slate-200 bg-white p-8 flex flex-col justify-end min-h-[220px] hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300 ${span || ""}`} data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            {img && <><img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /><div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/50 to-transparent" /></>}
            <div className="relative space-y-2">
              <Icon className={`h-7 w-7 ${img ? "text-[#FF6B00]" : "text-[#0A192F]"}`} />
              <h3 className={`text-lg sm:text-xl font-semibold ${img ? "text-white" : "text-[#0A192F]"}`}>{title}</h3>
              <p className={`text-sm ${img ? "text-slate-200" : "text-slate-600"}`}>{text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  { icon: ShieldCheck, title: "Book online", text: "Create an account and request a shipment in minutes. We review and confirm it." },
  { icon: MapPinned, title: "Track every checkpoint", text: "A timeline and live map show where your consignment is right now." },
  { icon: BellRing, title: "Get notified", text: "Receivers get an email each time the status changes." },
];

function HowItWorks() {
  return (
    <section className="bg-white border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-[1fr_2fr] gap-12">
        <div className="space-y-4">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00]">How it works</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">From booking to delivery, in plain sight</h2>
          <Link to="/book" className="inline-flex items-center gap-2 mt-4 px-6 py-3 bg-[#FF6B00] hover:bg-[#E05D00] text-white font-semibold rounded-sm transition-colors" data-testid="landing-book-cta">
            Book a shipment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-8">
          {STEPS.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="space-y-3">
              <span className="font-mono text-sm text-slate-400">0{i + 1}</span>
              <Icon className="h-8 w-8 text-[#0A192F]" />
              <h3 className="text-lg font-semibold text-[#0A192F]">{title}</h3>
              <p className="text-sm text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const PRESENCE_STATS = [
  { icon: Globe2, title: "Global Coverage", value: "160+", text: "countries worldwide" },
  { icon: Languages, title: "Translation Services", value: "60+", text: "languages supported" },
  { icon: BadgeCheck, title: "Security Vetting", value: "National", text: "security standards" },
  { icon: Building2, title: "Diplomatic Offices", value: "250+", text: "locations served" },
];

function GlobalPresence() {
  return (
    <section className="bg-[#0A192F] text-white relative overflow-hidden grain" data-testid="global-presence-section">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-start">
        <div className="space-y-6">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00]">Global presence</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Network Services</h2>
          <div className="space-y-4 text-slate-300 text-sm md:text-base leading-relaxed">
            <p>Our customers overseas require a global and local responsive secure support network, which we expertly deliver worldwide. In the United States, we provide translation and interpretation into more than 60 languages by experts who understand your legal, political and business environment. Our trusted staff are security cleared, we can ensure yours are too through our national security vetting service.</p>
            <p>Our global regional hubs offer a wide range of mission-critical technology, logistic, IT and security services to clients and its partners overseas. Through our overseas secure services to more than 250 diplomatic offices, across 160 countries, we support around 14,000 staff globally, as well as many more from other government departments co-located at posts under the One HMG ethos. We provide complete confidence that information and goods are where they need to be, when they need to be, uncompromised.</p>
            <p>Our United States based services includes translation, interpreting and national security vetting. Our translators and interpreters work from and into more than 60 languages and ensure your message is communicated accurately, consistently and professionally in a language your audience understands. We provide an efficient, one-stop shop for all your translation and interpreting requirements and have subject matter experts who know the legal, political and business environments you are working in.</p>
          </div>
        </div>
        <div className="space-y-6">
          <img src="/images/cargo-ship.jpg" alt="Container ship carrying cargo" className="w-full h-64 md:h-80 object-cover rounded-sm" />
          <div className="grid grid-cols-2 gap-4">
            {PRESENCE_STATS.map(({ icon: Icon, title, value, text }) => (
              <div key={title} className="border border-white/15 bg-white/5 backdrop-blur-md rounded-sm p-5" data-testid={`presence-stat-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="h-6 w-6 text-[#FF6B00] mb-3" />
                <p className="font-display text-2xl sm:text-3xl font-bold">{value}</p>
                <p className="text-sm text-slate-300">{text}</p>
                <p className="text-xs uppercase tracking-wider text-slate-400 mt-2">{title}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const ADVANTAGES = [
  { icon: Zap, title: "Fast & Reliable", text: "Our expedited shipping services ensure your cargo reaches its destination on time, every time, with real-time tracking capabilities." },
  { icon: Lock, title: "Secure Handling", text: "Advanced security protocols and careful handling procedures protect your valuable shipments throughout the entire logistics process." },
  { icon: Network, title: "Global Network", text: "Our extensive international network allows us to deliver seamless logistics solutions across borders with local expertise." },
];

function Advantages() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="advantages-section">
      <div className="max-w-2xl mb-12 space-y-3">
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF6B00]">Our advantages</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0A192F]">Why Choose Our Services</h2>
        <p className="text-slate-600">With decades of experience in the logistics industry, we offer reliable, secure, and efficient shipping solutions that meet your unique requirements.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {ADVANTAGES.map(({ icon: Icon, title, text }) => (
          <div key={title} className="group bg-white border border-slate-200 rounded-sm p-8 hover:border-[#FF6B00] hover:-translate-y-1 transition-[transform,border-color] duration-300" data-testid={`advantage-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="h-12 w-12 rounded-sm bg-orange-50 flex items-center justify-center mb-5 group-hover:bg-[#FF6B00] transition-colors"><Icon className="h-6 w-6 text-[#FF6B00] group-hover:text-white transition-colors" /></span>
            <h3 className="text-lg font-semibold text-[#0A192F] mb-2">{title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="bg-[#FF6B00] text-white" data-testid="cta-band">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
        <div className="max-w-2xl space-y-3">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">We will take care of your cargo and deliver them safe and on time.</h2>
          <p className="text-white/85">Experience premium shipping and logistics services with our dedicated team of professionals.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/book" className="px-6 py-3 bg-[#0A192F] hover:bg-[#112240] font-semibold rounded-sm transition-colors" data-testid="cta-book-btn">Book a shipment</Link>
          <a href={SITE.tel} className="px-6 py-3 bg-white text-[#0A192F] hover:bg-slate-100 font-semibold rounded-sm inline-flex items-center gap-2 transition-colors" data-testid="cta-call-btn"><Phone className="h-4 w-4" /> Call {SITE.phone}</a>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  return (<><Hero /><Services /><GlobalPresence /><Advantages /><HowItWorks /><CtaBand /></>);
}
